// ============================================================
// PhotosRecues — ce que la lecture automatique n'a pas su rattacher.
// ------------------------------------------------------------
// Ce n'est pas une corbeille, et le mot compte : c'est la MESURE de ce
// que la lecture de photo vaut. Le taux de rattachement automatique
// s'affiche en tête, mois par mois.
//
// Un repli invisible transforme un taux d'échec de 100 % en
// fonctionnement normal (invariant 24). Si la lecture cesse un jour de
// marcher — modèle changé, format de fiche modifié, photos plus
// sombres — cette liste se remplit et le pourcentage tombe. Ça se VOIT,
// ça ne se déduit pas.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api, fetchBlob } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';

function Taux({ lignes }) {
  if (!lignes?.length) return null;
  return (
    <p className="field__hint">
      Rattachées automatiquement :{' '}
      {lignes.map((l) => (
        <span key={l.mois}>
          {l.mois} <strong>{Math.round((l.rattachees / l.recues) * 100)} %</strong> ({l.rattachees}/
          {l.recues}){' '}
        </span>
      ))}
    </p>
  );
}

/**
 * La vignette, chargée par le client API.
 *
 * L'URL d'objet est révoquée au démontage : sans ça, chaque
 * rafraîchissement de la liste laisserait une image entière en mémoire
 * du navigateur, et l'écran se recharge toutes les dix secondes.
 */
function Vignette({ id, legende }) {
  const [url, setUrl] = useState(null);
  const [rate, setRate] = useState(false);

  useEffect(() => {
    let objet = null;
    let vivant = true;
    fetchBlob(`/atelier/photos/${id}/image`)
      .then((blob) => {
        if (!vivant) return;
        objet = URL.createObjectURL(blob);
        setUrl(objet);
      })
      .catch(() => vivant && setRate(true));
    return () => {
      vivant = false;
      if (objet) URL.revokeObjectURL(objet);
    };
  }, [id]);

  if (rate) return <p className="field__hint">Image indisponible.</p>;
  if (!url) return <div className="photo-recue__vignette photo-recue__vignette--vide" />;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img className="photo-recue__vignette" src={url} alt={`Fiche reçue le ${legende}`} />
    </a>
  );
}

export function PhotosRecues({ onRattache }) {
  const toast = useToast();
  const charger = useCallback(() => api.get('/atelier/photos'), []);
  const { data, loading, error, reload } = useResource(charger, []);
  const [choix, setChoix] = useState({});
  const [pieces, setPieces] = useState(null);
  const [busy, setBusy] = useState(null);

  async function chargerPieces() {
    if (pieces) return;
    try {
      const r = await api.get('/atelier/pieces-avec-fiche');
      setPieces(r.pieces);
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function rattacher(photo) {
    const pieceId = choix[photo.id];
    if (!pieceId) return;
    setBusy(photo.id);
    try {
      const r = await api.post(`/atelier/photos/${photo.id}/rattacher`, { piece_id: Number(pieceId) });
      toast.success(
        r.ecrits?.length
          ? `Rattachée — ${r.ecrits.length} temps écrit(s), à vérifier.`
          : 'Rattachée. Saisissez les temps dans la colonne « réel ».',
      );
      await reload();
      onRattache?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  }

  async function relire(photo) {
    setBusy(photo.id);
    try {
      const r = await api.post(`/atelier/photos/${photo.id}/relire`, {});
      toast.success(
        r.statut === 'rattachee'
          ? 'Relue et rattachée — les temps sont dans « à vérifier ».'
          : 'Relue, mais toujours pas rattachée. La raison est mise à jour.',
      );
      await reload();
      onRattache?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  }

  async function sansSuite(photo) {
    setBusy(photo.id);
    try {
      await api.post(`/atelier/photos/${photo.id}/sans-suite`, {});
      toast.success('Classée sans suite — elle reste consultable.');
      await reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <SkeletonRows rows={2} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Card>
      <CardHead title={`Photos reçues — ${data.photos.length} en attente`} />
      <CardBody className="stack">
        <p className="field__hint">
          Les fiches photographiées et envoyées au bot Telegram. Celles que le programme a su lire
          sont déjà dans « à vérifier » ; <strong>celles-ci lui ont résisté</strong> et attendent une
          main.
        </p>
        <Taux lignes={data.taux} />

        {data.photos.length === 0 ? (
          <EmptyState title="Rien en attente">
            Toutes les photos reçues ont été rattachées.
          </EmptyState>
        ) : null}

        {data.photos.map((photo) => (
          <div className="photo-recue" key={photo.id}>
            {/* La photo elle-même : on ne décide pas d'un rattachement
                sur une ligne de tableau, on regarde la feuille.
                Chargée par `fetchBlob` comme tous les fichiers de
                l'application — un `<img src>` vers l'autre origine
                marcherait aujourd'hui et casserait le jour où les
                cookies se durcissent. */}
            <Vignette id={photo.id} legende={formatDateTime(photo.created_at)} />

            <div className="stack">
              <p>
                <strong>{photo.expediteur || 'Expéditeur inconnu'}</strong> ·{' '}
                {formatDateTime(photo.created_at)}
                {photo.ref_lue ? (
                  <>
                    {' '}
                    · lu <Badge tone="info">{photo.ref_lue}</Badge>
                  </>
                ) : null}
              </p>
              {/* POURQUOI ça n'a pas marché, en clair. Sans cette phrase,
                  il faudrait rouvrir les journaux pour traiter la photo. */}
              {photo.erreur ? (
                <p className="warnings" role="status">
                  {photo.erreur}
                </p>
              ) : null}
              {photo.legende ? <p className="field__hint">« {photo.legende} »</p> : null}

              <div className="photo-recue__actions">
                <label className="field">
                  <span className="field__label">Rattacher à la pièce</span>
                  <select
                    className="select"
                    value={choix[photo.id] ?? ''}
                    onFocus={chargerPieces}
                    onChange={(e) => setChoix((c) => ({ ...c, [photo.id]: e.target.value }))}
                  >
                    <option value="">Choisir…</option>
                    {(pieces ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.ref_interne} — {p.name || p.ref || 'pièce'} ({p.devis_reference})
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!choix[photo.id] || busy === photo.id}
                  onClick={() => rattacher(photo)}
                >
                  Rattacher
                </Button>
                {/* Relire : une panne de NOTRE côté ne doit pas obliger
                    l'atelier à rephotographier une feuille déjà
                    repartie. */}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy === photo.id}
                  onClick={() => relire(photo)}
                >
                  Relire
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === photo.id}
                  onClick={() => sansSuite(photo)}
                >
                  Sans suite
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

export default PhotosRecues;
